import { gap } from '../content';
import { heroUnits, type HeroUnit } from '../heroUnits';
import { Swipe, cx } from '../ui';
import { TheGap, type Tone } from './TheGap';

// The full "gap" section: eyebrow, title (one swipe, on "factory gate"), lede beside it, the animated diagram
// (the support call is its payoff), then the answer in one short row. Drop it straight into any page.
// compact: for secondary pages. Title + a one-line lede, no answer row; the diagram's last beat carries the answer.

const COMPACT_LEDE = 'Once it leaves, nobody knows who has it. Every support call starts from nothing.';

function Title() {
  const [a, b] = gap.title;
  const phrase = 'factory gate';
  const at = b.indexOf(phrase);
  return (
    <h2 className="t-h2 bm-gap-title">
      {a}
      <br className="bm-gap-br" />{' '}
      {at < 0 ? (
        b
      ) : (
        <>
          {b.slice(0, at)}
          <Swipe>{phrase}</Swipe>
          {b.slice(at + phrase.length)}
        </>
      )}
    </h2>
  );
}

export function GapSection({
  tone = 'light',
  unit = heroUnits[0],
  id = 'the-gap',
  className,
  compact = false,
}: {
  tone?: Tone;
  unit?: HeroUnit;
  id?: string;
  className?: string;
  /** Secondary pages: title + one-line lede, no answer row. Default: full. */
  compact?: boolean;
}) {
  const dark = tone === 'dark';
  return (
    <section
      id={id}
      className={cx('section bm-gap-section', dark && 'on-dark', compact && 'is-compact', className)}
      style={{ background: dark ? '#241E61' : '#fff' }}
    >
      <style>{SECTION_CSS}</style>
      <div className="wrap">
        <div className="bm-gap-head">
          <div>
            <Title />
          </div>
          <p className="t-lede bm-gap-lede">{compact ? COMPACT_LEDE : gap.lede}</p>
        </div>

        <TheGap tone={tone} unit={unit} compact={compact} answerCaption={compact} />

        {!compact && (
          <div className="bm-gap-answer">
            <h3 className="bm-gap-answer-t">
              {gap.answer.title[0]} <br className="bm-gap-br2" />
              {gap.answer.title[1]}
            </h3>
            <p className="bm-gap-answer-b">{gap.answer.body}</p>
          </div>
        )}
      </div>
    </section>
  );
}

const SECTION_CSS = `
.v2 .bm-gap-section{padding-top:clamp(60px,6.5vw,96px);padding-bottom:clamp(60px,6.5vw,96px)}
.bm-gap-section .bm-gap-head{display:grid;grid-template-columns:minmax(0,1fr);gap:16px;margin-bottom:clamp(26px,3vw,40px)}
.bm-gap-section .bm-gap-title{margin-top:0}
.bm-gap-section .bm-gap-lede{margin:0;max-width:60ch}
.bm-gap-section .bm-gap-answer{display:grid;grid-template-columns:minmax(0,1fr);gap:12px;margin-top:clamp(26px,3vw,40px);align-items:start}
.bm-gap-section .bm-gap-answer-t{margin:0;font-weight:800;font-size:clamp(24px,2.6vw,34px);line-height:1.08;letter-spacing:-.035em;color:#241E61}
.bm-gap-section.on-dark .bm-gap-answer-t{color:#fff}
.bm-gap-section .bm-gap-answer-b{margin:0;font-size:16.5px;line-height:1.6;color:#67648F;max-width:56ch}
.bm-gap-section.on-dark .bm-gap-answer-b{color:rgba(255,255,255,.72)}
@media (min-width:960px){
  .bm-gap-section .bm-gap-head{grid-template-columns:minmax(0,1.7fr) minmax(0,1fr);gap:48px;align-items:end}
  .bm-gap-section .bm-gap-title{font-size:clamp(34px,3.55vw,50px)}
  .bm-gap-section .bm-gap-answer{grid-template-columns:minmax(0,1.45fr) minmax(0,1fr);gap:56px}
  .bm-gap-section .bm-gap-lede{font-size:18px}
}
.bm-gap-section .bm-gap-br,.bm-gap-section .bm-gap-br2{display:none}
@media (min-width:700px){.bm-gap-section .bm-gap-br2{display:inline}}
@media (min-width:700px){.bm-gap-section .bm-gap-br{display:inline}}
@media (max-width:420px){.bm-gap-section .wrap{padding-left:16px;padding-right:16px}}
`;
